import { useMemo, useRef, useCallback, useEffect } from 'react';
import { DEFAULT_GRID_CONFIG, directionRowsFor } from '../lib/courseGridConfig.js';
import { legDetails } from '../lib/geo.js';
import { calculateFullDistance } from '../lib/roundingInference.js';
import { canonicalizeMarkIds } from '../lib/coursePreviewLayout.js';
import './CardGrid.css';

/**
 * Build a lookup from markId → [lat, lng].
 */
export function buildMarkLookup(marks) {
  const lookup = {};
  for (const m of marks) {
    if (m.latitude != null && m.longitude != null) {
      lookup[m.id] = [m.latitude, m.longitude];
    }
  }
  return lookup;
}

/**
 * Calculate total course distance in nm from mark positions.
 */
export function calcCourseDistance(course, markLookup) {
  if (!course.markIds || course.markIds.length < 2) return 0;
  const legs = legDetails(course.markIds, markLookup);
  return legs.reduce((sum, leg) => sum + (leg.distNm || 0), 0);
}

/**
 * Calculate full course distance including start/finish legs.
 * Falls back to mark-to-mark distance if no line config provided.
 */
function calcFullDistance(course, markLookup, cardLine) {
  if (!course.markIds || course.markIds.length < 2) return 0;
  if (!cardLine) return calcCourseDistance(course, markLookup);

  // Resolve course-level overrides on top of card line
  const startLine = {
    type: course.startType ?? cardLine.startType,
    offsetNm: course.startOffsetNm ?? cardLine.startOffsetNm,
    markA: course.startMarkA ?? cardLine.startMarkA,
    markB: course.startMarkB ?? cardLine.startMarkB,
  };
  const finishLine = {
    type: course.finishType ?? cardLine.finishType,
    offsetNm: course.finishOffsetNm ?? cardLine.finishOffsetNm,
    markA: course.finishMarkA ?? cardLine.finishMarkA,
    markB: course.finishMarkB ?? cardLine.finishMarkB,
  };

  const result = calculateFullDistance(course.markIds, markLookup, startLine, finishLine);
  return result.totalNm ?? calcCourseDistance(course, markLookup);
}

/**
 * Check if a course has validation errors.
 */
export function isCourseInvalid(course, markLookup) {
  if (!course.markIds || course.markIds.length === 0) return true;
  return course.markIds.some((id) => !markLookup[id]);
}

/**
 * Map a normalized value (0-1) to a background color using navy at varying opacity.
 */
function distanceColor(normalized) {
  const opacity = 0.08 + normalized * 0.32; // range 0.08-0.40
  return `rgba(26, 26, 46, ${opacity.toFixed(3)})`;
}

/**
 * Club-configurable direction/number grid for a single course card. Defaults to
 * the DBSC 16×8 A–R letter grid; a `gridConfig` reshapes it to 16/8/4 directions
 * and per-direction or sector-gapped numbering. (ra-gbrd.20)
 *
 * @param {Object} props
 * @param {Array} props.courses - Courses for this card
 * @param {Array} props.marks - Club marks with coordinates
 * @param {{ dir: string, num: number } | null} props.selectedCell - Currently selected cell
 * @param {(dir: string, num: number) => void} props.onSelectCell - Selection callback
 * @param {object} [props.gridConfig] - Club grid config (defaults to DBSC 16×8)
 */
export default function CardGrid({ courses, marks, selectedCell, onSelectCell, cardLine, gridConfig }) {
  const gridRef = useRef(null);
  const markLookup = useMemo(() => buildMarkLookup(marks || []), [marks]);

  const config = gridConfig || DEFAULT_GRID_CONFIG;
  const rows = useMemo(() => directionRowsFor(config), [config]);
  const numCols = config.coursesPerDirection ?? DEFAULT_GRID_CONFIG.coursesPerDirection;
  const colHeaders = useMemo(() => Array.from({ length: numCols }, (_, i) => i + 1), [numCols]);

  const courseMap = useMemo(() => {
    const map = new Map();
    for (const c of courses || []) {
      const key = `${c.directionCode}${c.courseNumber}`;
      // Canonicalize markIds to the marks' real case so courses corrupted by the
      // old lowercasing bug (ra-or1y: ['n','m','c'] vs uppercase marks) don't
      // render as invalid / no-distance in the grid. Every downstream consumer
      // (distance, validity) reads from this map. (codex review on 43f7def6.)
      map.set(key, { ...c, markIds: canonicalizeMarkIds(c.markIds, marks) });
    }
    return map;
  }, [courses, marks]);

  // Compute distances for all courses, then get min/max for color scaling
  const distances = useMemo(() => {
    const dists = new Map();
    for (const [key, course] of courseMap) {
      dists.set(key, calcFullDistance(course, markLookup, cardLine));
    }
    return dists;
  }, [courseMap, markLookup, cardLine]);

  const distRange = useMemo(() => {
    const vals = [...distances.values()].filter((d) => d > 0);
    if (vals.length === 0) return { min: 0, max: 1 };
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    if (min === max) return { min, max: min + 1 };
    return { min, max };
  }, [distances]);

  // Arrow key navigation: move selection between adjacent cells. Works in
  // (row, col) index space so it is correct for gapped numbering, where a row's
  // course numbers (e.g. 11,12,13,14) aren't 1-based.
  const handleKeyDown = useCallback((e) => {
    if (!selectedCell && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      // No cell selected — enter grid at the first cell
      e.preventDefault();
      onSelectCell(rows[0].dir, rows[0].numbers[0]);
      return;
    }
    if (!selectedCell) return;

    const rowIdx = rows.findIndex((r) => r.dir === selectedCell.dir);
    if (rowIdx < 0) return;
    const colIdx = rows[rowIdx].numbers.indexOf(selectedCell.num);
    if (colIdx < 0) return;
    let nextRow = rowIdx;
    let nextCol = colIdx;

    switch (e.key) {
      case 'ArrowUp':
        nextRow = Math.max(0, rowIdx - 1);
        break;
      case 'ArrowDown':
        nextRow = Math.min(rows.length - 1, rowIdx + 1);
        break;
      case 'ArrowLeft':
        nextCol = Math.max(0, colIdx - 1);
        break;
      case 'ArrowRight':
        nextCol = Math.min(rows[rowIdx].numbers.length - 1, colIdx + 1);
        break;
      default:
        return; // Don't prevent default for other keys (Tab, etc.)
    }

    e.preventDefault();
    const target = rows[nextRow];
    onSelectCell(target.dir, target.numbers[nextCol]);
  }, [selectedCell, onSelectCell, rows]);

  // Focus the selected cell when selection changes
  useEffect(() => {
    if (!selectedCell || !gridRef.current) return;
    const cell = gridRef.current.querySelector(
      `[data-dir="${selectedCell.dir}"][data-num="${selectedCell.num}"]`,
    );
    if (cell) cell.focus();
  }, [selectedCell]);

  return (
    <div className="card-grid-wrap">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="card-grid"
        role="grid"
        aria-label="Course grid"
        ref={gridRef}
        onKeyDown={handleKeyDown}
        // Column count is config-driven; the CSS default (8) only fits the DBSC
        // grid, so set the template explicitly for 16/8/4 + custom widths.
        style={{ gridTemplateColumns: `auto repeat(${numCols}, 1fr)` }}
      >
        {/* Corner spacer */}
        <div className="card-grid-corner" />

        {/* Column headers: variation index 1..N */}
        {colHeaders.map((num) => (
          <div key={`h-${num}`} className="card-grid-col-header" role="columnheader">
            {num}
          </div>
        ))}

        {/* Data rows */}
        {rows.map((row, idx) => (
          <GridRow
            key={row.dir}
            row={row}
            isEntryRow={idx === 0}
            courseMap={courseMap}
            distances={distances}
            distRange={distRange}
            markLookup={markLookup}
            selectedCell={selectedCell}
            onSelectCell={onSelectCell}
          />
        ))}
      </div>
    </div>
  );
}

function GridRow({ row, isEntryRow, courseMap, distances, distRange, markLookup, selectedCell, onSelectCell }) {
  const { dir, label, cardinal, numbers } = row;
  // Show just the letter and cardinal direction, e.g. "A N"
  const shortLabel = `${dir} ${cardinal}`;

  return (
    <>
      <div className="card-grid-row-label" role="rowheader" title={label}>
        {shortLabel}
      </div>
      {numbers.map((num, colIdx) => {
        const key = `${dir}${num}`;
        const course = courseMap.get(key);
        const dist = distances.get(key) || 0;
        const isSelected =
          selectedCell && selectedCell.dir === dir && selectedCell.num === num;

        // Roving tabindex: only selected cell (or the first cell by default) is tabbable
        const isDefaultEntry = !selectedCell && isEntryRow && colIdx === 0;
        const tabIndex = isSelected || isDefaultEntry ? 0 : -1;

        if (!course) {
          return (
            <button
              key={key}
              className={`card-grid-cell card-grid-cell--empty${isSelected ? ' card-grid-cell--selected' : ''}`}
              onClick={() => onSelectCell(dir, num)}
              title={`${key} — ${label} — Click to add`}
              aria-label={`Course ${key}, empty`}
              aria-pressed={isSelected || undefined}
              tabIndex={tabIndex}
              data-dir={dir}
              data-num={num}
            >
              &middot;
            </button>
          );
        }

        const markCount = course.markIds ? course.markIds.length : 0;
        const invalid = isCourseInvalid(course, markLookup);
        const normalized =
          dist > 0 ? (dist - distRange.min) / (distRange.max - distRange.min) : 0;
        const bgColor = dist > 0 ? distanceColor(normalized) : '#e8e8f0';
        const darkBg = normalized > 0.5;

        const seq = course.markIds ? course.markIds.join('') : '';
        const tooltip = `${key} — ${markCount} marks — ${seq} — ${dist > 0 ? dist.toFixed(1) + ' nm' : 'no distance'}`;

        const classes = [
          'card-grid-cell',
          'card-grid-cell--filled',
          darkBg ? 'card-grid-cell--dark-bg' : '',
          isSelected ? 'card-grid-cell--selected' : '',
          invalid ? 'card-grid-cell--invalid' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <button
            key={key}
            className={classes}
            style={{ backgroundColor: bgColor }}
            onClick={() => onSelectCell(dir, num)}
            title={tooltip}
            aria-label={`Course ${key}, ${markCount} marks, ${dist > 0 ? dist.toFixed(1) + ' nautical miles' : 'no distance'}`}
            aria-pressed={isSelected || undefined}
            tabIndex={tabIndex}
            data-dir={dir}
            data-num={num}
          >
            {dist > 0 ? dist.toFixed(1) : '—'}
          </button>
        );
      })}
    </>
  );
}
